import React, { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useHanziStore } from '../hooks/useHanziConfig'
import { createShortLink } from '../utils/share-api'

const ActionBar: React.FC = () => {
  const { t } = useTranslation()
  const config = useHanziStore((state) => state.config)
  const [showShareMenu, setShowShareMenu] = useState(false)
  const [shortUrl, setShortUrl] = useState<string | null>(null)

  const handlePrint = () => {
    window.print()
  }

  const EXPORT_SCALE = 1.5

  const getHighResCanvas = (canvas: HTMLCanvasElement): HTMLCanvasElement => {
    const scaledCanvas = document.createElement('canvas')
    scaledCanvas.width = canvas.width * EXPORT_SCALE
    scaledCanvas.height = canvas.height * EXPORT_SCALE
    const ctx = scaledCanvas.getContext('2d')
    if (ctx) {
      ctx.scale(EXPORT_SCALE, EXPORT_SCALE)
      ctx.drawImage(canvas, 0, 0)
    }
    return scaledCanvas
  }

  const handleDownloadImage = () => {
    const canvases = document.querySelectorAll('canvas')
    if (canvases.length === 0) return

    if (canvases.length === 1) {
      const link = document.createElement('a')
      link.download = `chinese-practice-${Date.now()}.png`
      link.href = getHighResCanvas(canvases[0]).toDataURL('image/png')
      link.click()
      return
    }

    canvases.forEach((canvas, index) => {
      const link = document.createElement('a')
      link.download = `chinese-practice-${Date.now()}-page-${index + 1}.png`
      link.href = getHighResCanvas(canvas).toDataURL('image/png')
      link.click()
    })
  }

  const handleDownloadPDF = async () => {
    const canvases = document.querySelectorAll('canvas')
    if (canvases.length === 0) return

    try {
      const { jsPDF } = await import('jspdf')
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: config.paperSize === 'letter' ? 'letter' : 'a4',
      })

      for (let i = 0; i < canvases.length; i++) {
        if (i > 0) pdf.addPage()
        const imgData = getHighResCanvas(canvases[i]).toDataURL('image/jpeg', 0.95)
        const imgProps = pdf.getImageProperties(imgData)
        const pdfWidth = pdf.internal.pageSize.getWidth()
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width
        pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight)
      }

      pdf.save(`chinese-practice-${Date.now()}.pdf`)
    } catch (err) {
      console.error('PDF generation failed:', err)
      alert(t('toast.pdfError'))
    }
  }

  const getShareText = useCallback(() => {
    return `${t('app.title')} - ${t('app.description')}`
  }, [t])

  const generateShortUrl = useCallback(async (): Promise<string> => {
    if (shortUrl) return shortUrl
    try {
      const result = await createShortLink(config)
      if (result.ok && result.url) {
        setShortUrl(result.url)
        return result.url
      }
    } catch {
    }
    return window.location.href
  }, [config, shortUrl])

  const handleNativeShare = async () => {
    const url = await generateShortUrl()
    const shareData = {
      title: t('app.title'),
      text: t('app.description'),
      url,
    }

    if (navigator.share) {
      try {
        await navigator.share(shareData)
        setShowShareMenu(false)
      } catch {
        handleCopyLink()
      }
    } else {
      handleCopyLink()
    }
  }

  const handleCopyLink = async () => {
    try {
      const url = await generateShortUrl()
      await navigator.clipboard.writeText(url)
      alert(t('toast.copySuccess'))
      setShowShareMenu(false)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const openShareWindow = (url: string) => {
    window.open(url, '_blank', 'width=600,height=400,scrollbars=yes')
    setShowShareMenu(false)
  }

  const handleFacebookShare = async () => {
    const shareUrl = await generateShortUrl()
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`
    openShareWindow(url)
  }

  const handleTwitterShare = async () => {
    const shareUrl = await generateShortUrl()
    const url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(getShareText())}`
    openShareWindow(url)
  }

  const handleWhatsAppShare = async () => {
    const shareUrl = await generateShortUrl()
    const url = `https://wa.me/?text=${encodeURIComponent(getShareText() + ' ' + shareUrl)}`
    openShareWindow(url)
  }

  const handleEmailShare = async () => {
    const shareUrl = await generateShortUrl()
    const subject = encodeURIComponent(t('app.title'))
    const body = encodeURIComponent(`${getShareText()}\n\n${shareUrl}`)
    window.location.href = `mailto:?subject=${subject}&body=${body}`
    setShowShareMenu(false)
  }

  return (
    <div className="fixed bottom-[100px] right-4 z-50 flex flex-col items-end gap-3 no-print">
      <button
        onClick={handlePrint}
        className="group relative flex w-12 h-12 items-center justify-center rounded-full bg-primary text-white shadow-lg hover:bg-primary-dark hover:scale-105 hover:shadow-2xl active:scale-95 transition-all duration-200"
        title={t('actions.print')}
        aria-label={t('actions.print')}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
        </svg>
        <span className="pointer-events-none absolute right-full top-1/2 mr-3 -translate-y-1/2 whitespace-nowrap rounded-lg bg-text px-2.5 py-1.5 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity duration-200 group-hover:opacity-100">
          {t('actions.print')}
        </span>
      </button>

      <button
        onClick={handleDownloadPDF}
        className="group relative flex w-12 h-12 items-center justify-center rounded-full bg-white text-text-secondary shadow-lg hover:text-primary hover:scale-105 hover:shadow-2xl active:scale-95 transition-all duration-200"
        title={t('actions.downloadPDF')}
        aria-label={t('actions.downloadPDF')}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
        <span className="pointer-events-none absolute right-full top-1/2 mr-3 -translate-y-1/2 whitespace-nowrap rounded-lg bg-text px-2.5 py-1.5 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity duration-200 group-hover:opacity-100">
          {t('actions.downloadPDF')}
        </span>
      </button>

      <button
        onClick={handleDownloadImage}
        className="group relative flex w-12 h-12 items-center justify-center rounded-full bg-white text-text-secondary shadow-lg hover:text-primary hover:scale-105 hover:shadow-2xl active:scale-95 transition-all duration-200"
        title={t('actions.downloadImage')}
        aria-label={t('actions.downloadImage')}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <span className="pointer-events-none absolute right-full top-1/2 mr-3 -translate-y-1/2 whitespace-nowrap rounded-lg bg-text px-2.5 py-1.5 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity duration-200 group-hover:opacity-100">
          {t('actions.downloadImage')}
        </span>
      </button>

      <div className="relative">
        <button
          onClick={() => setShowShareMenu(!showShareMenu)}
          className={`group relative flex w-12 h-12 items-center justify-center rounded-full bg-white text-text-secondary shadow-lg hover:text-primary hover:scale-105 hover:shadow-2xl active:scale-95 transition-all duration-200 ${
            showShareMenu ? 'text-primary shadow-2xl' : ''
          }`}
          title={t('actions.share')}
          aria-label={t('actions.share')}
          aria-expanded={showShareMenu}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
          </svg>
          <span className="pointer-events-none absolute right-full top-1/2 mr-3 -translate-y-1/2 whitespace-nowrap rounded-lg bg-text px-2.5 py-1.5 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity duration-200 group-hover:opacity-100">
            {t('actions.share')}
          </span>
        </button>

        {showShareMenu && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setShowShareMenu(false)}
            />
            <div className="absolute bottom-full right-0 z-50 mb-2 min-w-[220px] overflow-hidden rounded-xl border border-border bg-white p-2 shadow-2xl origin-bottom-right">
              <div className="px-3 py-2 text-xs font-semibold text-text-light uppercase tracking-wider">
                {t('actions.share')}
              </div>

              {typeof navigator.share === 'function' && (
                <button
                  onClick={handleNativeShare}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-text-secondary hover:bg-primary/5 rounded-lg transition-colors text-left"
                >
                  <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                  <span className="font-medium">{t('actions.nativeShare')}</span>
                </button>
              )}

              <button
                onClick={handleCopyLink}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-text-secondary hover:bg-primary/5 rounded-lg transition-colors text-left"
              >
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <span className="font-medium">{t('actions.shareLink')}</span>
              </button>

              <div className="h-px bg-border my-1.5 mx-2" />

              <button
                onClick={handleFacebookShare}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-text-secondary hover:bg-primary/5 rounded-lg transition-colors text-left"
              >
                <svg className="w-5 h-5 text-[#1877F2]" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
                <span className="font-medium">{t('actions.shareFacebook')}</span>
              </button>

              <button
                onClick={handleTwitterShare}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-text-secondary hover:bg-primary/5 rounded-lg transition-colors text-left"
              >
                <svg className="w-5 h-5 text-black" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
                <span className="font-medium">{t('actions.shareTwitter')}</span>
              </button>

              <button
                onClick={handleWhatsAppShare}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-text-secondary hover:bg-primary/5 rounded-lg transition-colors text-left"
              >
                <svg className="w-5 h-5 text-[#25D366]" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                <span className="font-medium">{t('actions.shareWhatsApp')}</span>
              </button>

              <button
                onClick={handleEmailShare}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-text-secondary hover:bg-primary/5 rounded-lg transition-colors text-left"
              >
                <svg className="w-5 h-5 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="font-medium">{t('actions.shareEmail')}</span>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default ActionBar
